import React, {useEffect, useState, useRef} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark, faCircleCheck } from "@fortawesome/free-solid-svg-icons";

const steps = [
  {
    number: "1",
    title: "Define Your Problem",
    desc: "Input your problem, set debate parameters, and customize AI personas to match the expertise your challenge demands.",
  },
  {
    number: "2",
    title: "AI Personas Debate",
    desc: "Multiple expert AI personas engage in structured iterative debate rounds—each challenging your problem from a unique perspective.",
  },
  {
    number: "3",
    title: "Synthesized Intelligence",
    desc: "The Super Persona synthesizes every debate output into a single coherent recommendation—validated by human moderator oversight for quality, relevance, and ethical integrity.",
  },
];


const cards = [
  {
    img: "corporate.png",
    title: "Corporate Strategy",
    desc: "Your board is ready to acquire a competitor. Finance sees upside. Operations is cautious. Legal flags risks, but no one's talking to each other. Omnix runs the decision through analytical, tactical, and contrarian personas in one debate. Hidden integration costs surface immediately. You restructure the deal before signing, and the merger succeeds because every assumption was stress-tested early.",
  },
  {
    img: "policy.png",
    title: "Policy Development",
    desc: "A new healthcare policy passes every internal review, your team is confident. Then Omnix debates it through economic, social, and operational lenses simultaneously. Unintended consequences for rural communities emerge that your advisory board completely missed. You adjust the policy before launch. Implementation runs smoothly because the blind spots were caught when they still mattered.",
  },
  {
    img: "research.png",
    title: "Research",
    desc: "Your research team is stuck debating methodology, qualitative versus quantitative, sample size, control variables. Six weeks of meetings with no consensus. Omnix structures the entire debate through statistical, ethical, and practical personas in one afternoon. Every argument gets challenged. Every assumption gets tested. What dragged for weeks resolves in hours, and your paper publishes faster because the thinking was sharper from the start.",
  },
];

const Section4 = ({url}) => {

    const sectionRef = useRef(null);
  const [Slick, setSlick] = useState(null);

  // Prevent multiple loads
    const slickLoaded = useRef(false);
  
    useEffect(() => {
      const observer = new IntersectionObserver(
        async (entries) => {
          const entry = entries[0];
  
          if (entry.isIntersecting && !slickLoaded.current) {
            slickLoaded.current = true; // mark loaded BEFORE loading
  
            // Dynamically load JS + CSS
            const slickModule = await import("react-slick");
            await import("slick-carousel/slick/slick.css");
            await import("slick-carousel/slick/slick-theme.css");
  
            setSlick(() => slickModule.default);
  
            observer.disconnect(); // stop observing forever
          }
        },
        { threshold: 0.2 }
      );
  
      if (sectionRef.current) observer.observe(sectionRef.current);
      return () => observer.disconnect();
    }, []);
  
    const settings = {
      dots: true,              // ✅ dots enabled
      arrows: false,           // ❌ no arrows
      infinite: true,
      slidesToShow: 3,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 3000,
      pauseOnHover: true,
      speed: 600,

      responsive: [
        { breakpoint: 1200, settings: { slidesToShow: 2 } },
        { breakpoint: 768, settings: { slidesToShow: 1 } }, // ✅ mobile
      ],
    };

  return (
    <section className="home-section-4" ref={sectionRef} 
     style={{
        backgroundImage: `url("${url}images/omnix/banner.jpeg")`,
         backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
    }}
    >
      <div className="container">

        {/* Heading */}
        <div className="section-header text-center">
          <h2>
            Real Problems. Real Decisions. <br />
            <span>Real Results.</span>
          </h2>
          <p>
            See how organizations use Omnix to decide smarter with our multi-persona AI debates
          </p>
        </div>

        {/* Row 1 */}
        <div className="row d-flex justify-content-center align-items-center content-row">
          <div className="col-lg-5">
            <div className="image-box">
              <img
               src={`${url}images/omnix/corporate.png`}
                alt="strategy"
                data-aos="flip-up"
                data-aos-duration="1000"
              />
            </div>
          </div>

          <div className="col-lg-5 text-col">
            <h5>Corporate Strategy</h5>
            <p className="me-lg-4">
              Your board is ready to acquire a competitor. Finance sees upside. Operations is cautious. Legal flags risks, but no one's talking to each other. Omnix runs the decision through analytical, tactical, and contrarian personas in one debate. Hidden integration costs surface immediately. You restructure the deal before signing, and the merger succeeds because every assumption was stress-tested early.
            </p>
          </div>
        </div>

        {/* Row 2 */}

        <div className="row d-flex justify-content-center align-items-center content-row">
          <div className="col-lg-5 order-1 order-lg-2">
            <div className="image-box ">
             <img
              src={`${url}images/omnix/policy.png`}
                alt="policy"
                data-aos="flip-up"
                data-aos-duration="1000"
                />
            </div>
          </div>

          <div className="col-lg-5 text-col order-2 order-lg-1 ">
            <h5>Policy Development</h5>
            <p className="me-lg-4">
              A new healthcare policy passes every internal review, your team is confident. Then Omnix debates it through economic, social, and operational lenses simultaneously. Unintended consequences for rural communities emerge that your advisory board completely missed. You adjust the policy before launch. Implementation runs smoothly because the blind spots were caught when they still mattered.
            </p>
          </div>
        </div>


        {/* Row 3 */}
        <div className="row d-flex justify-content-center align-items-center content-row">
          <div className="col-lg-5">
            <div className="image-box">
              <img
               src={`${url}images/omnix/research.png`}
                alt="research"
                data-aos="flip-up"
                data-aos-duration="1000"
              />
            </div>
          </div>

          <div className="col-lg-5 text-col">
            <h5>Research</h5>
            <p className="me-lg-4">
              Your research team is stuck debating methodology, qualitative versus quantitative, sample size, control variables. Six weeks of meetings with no consensus. Omnix structures the entire debate through statistical, ethical, and practical personas in one afternoon. Every argument gets challenged. Every assumption gets tested. What dragged for weeks resolves in hours, and your paper publishes faster because the thinking was sharper from the start.
            </p>
          </div>
        </div>

        <div className="mobile-slider d-md-none" ref={sectionRef}>
        {Slick ? (
          <Slick {...settings}>
            {cards.map((card, index) => (
              <div key={index} className="slide-card">
                
                <div className="card-inner">
                  <img
                    src={`${url}images/omnix/${card.img}`}
                    alt={card.title}
                  />

                  <div className="card-content">
                    <h5>{card.title}</h5>
                    <p>{card.desc}</p>
                  </div>
                </div>

              </div>
            ))}
          </Slick>
        ) : (
          <div style={{ height: "300px" }}></div>
        )}
      </div>

      </div>
      <div className="container home-section-5">

        {/* Heading */}
        <div className="section-header text-center">
          <h2>
            Traditional vs <span>Omnix Comparison</span>
          </h2>
        </div>

        <div className="row comparison-row">

          {/* LEFT CARD */}
          <div className="col-lg-6 col-xl-5 overflow-hidden">
            <div className="comparison-card left-card">

              <div className="card-header">
                <FontAwesomeIcon icon={faCircleXmark} className="icon red" />
                <h5>Traditional Decision Making Tools</h5>
              </div>

              <ul>
                <li>Slow expert panels that take weeks to assemble and even longer to reach consensus.</li>
                <li>Expensive consultants delivering generic recommendations without deep contextual understanding.</li>
                <li>No structured stress-testing of ideas before committing to high-stakes execution.</li>
                <li>Decisions based on incomplete information leaving critical blind spots unchallenged.</li>
                <li>No iterative refinement—once the report is delivered, the thinking stops.</li>
              </ul>

            </div>
          </div>

          {/* RIGHT CARD */}
          <div className="col-lg-6 col-xl-5 overflow-hidden">
            <div className="comparison-card right-card">

              <div className="card-header">
                <FontAwesomeIcon icon={faCircleCheck} className="icon green" />
                <h5>Omnix Multi Agent AI Platform</h5>
              </div>

              <ul>
                <li>Instant structured insights delivered through rigorous iterative debate rounds in minutes not weeks.</li>
                <li>Scalable across industries—from corporate strategy to policy development and research.</li>
                <li>Continuous iterative refinement that builds deeper intelligence with every debate session.</li>
                <li>Data-backed confident decisions are synthesized by Super Persona into one clear, actionable output.</li>
                <li>Always evolving—iterative memory ensures Omnix gets smarter with every interaction.</li>
              </ul>

            </div>
          </div>

        </div>

      </div>

      {/* <div className="container home-section-6">

        <div className="section-header text-center">
          <h2>
            From Complex Problem to <br />
            <span>Confident Decision</span> in Three Steps
          </h2>

          <p>
           Transforms your most complex challenges into clear, data-backed decisions through a structured AI decision-making process
          </p>
        </div>

 
        <div className="steps-wrapper">

          <div className="line" data-aos="fade-right" data-aos-duration="2000"></div>

          <div className="row">

            {steps.map((step, index) => (
              <div className="col-md-4 text-center step-col" key={index} data-aos="zoom-in" data-aos-duration="1000" data-aos-delay={index * 300}>

                <div className="step-number">
                  {step.number}
                </div>

                <h5>{step.title}</h5>
                <p>{step.desc}</p>

              </div>
            ))}

          </div>
        </div>

      </div> */}


    </section>
  );
};

export default Section4;